import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, input, output, signal } from '@angular/core';

import { ENotificationCode } from '@ta/notification';
import { TranslatePipe } from '@ta/translation';
import { LoaderComponent, TitleComponent, ToggleCardComponent } from '@ta/ui';

import { TaPermissionsService } from '@ta/user';

import { BaseComponent } from 'src/app/features/core/abstract/baseComponent';
import { OwnerService } from 'src/app/services/user/owner.service';
import { TenantService } from 'src/app/services/user/tenant.service';

export interface RoleSelectionState {
  isTenantActive: boolean;
  isOwnerActive: boolean;
}

@Component({
  selector: 'app-role-selection',
  templateUrl: './role-selection.component.html',
  styleUrls: ['./role-selection.component.scss'],
  standalone: true,
  imports: [TitleComponent, ToggleCardComponent, LoaderComponent, TranslatePipe],
})
export class RoleSelectionComponent extends BaseComponent implements OnInit {
  public showTitle = input<boolean>(true);

  public roleChange = output<RoleSelectionState>();

  private _tenantService = inject(TenantService);
  private _ownerService = inject(OwnerService);
  private _permissionsService = inject(TaPermissionsService);

  public isTenantActive = signal(false);
  public isOwnerActive = signal(false);

  ngOnInit() {
    this._registerSubscription(
      this._userService.userDetail.get$().subscribe({
        next: (user) => {
          this.isTenantActive.set(!!(user?.tenant?.id ?? false));
          this.isOwnerActive.set(!!(user?.owner?.id ?? false));
        },
      }),
    );

    this._fetch();
  }

  public onTenantToggle(isActive: boolean) {
    if (!this._atLeastOne(isActive, this.isOwnerActive())) {
      return;
    }
    this.isTenantActive.set(isActive);
    this.requestState.asked();
    if (isActive) {
      this._registerSubscription(
        this._tenantService.createTenantLink$().subscribe({
          complete: () => {
            this.requestState.completed();
            this._notificationService.addNotification(
              'app.notifications.messages.roles.tenant_activated',
              ENotificationCode.success,
            );
            this._emitRoleChange();
          },
          error: (error: HttpErrorResponse) => {
            this.requestState.onError(error.status, error.statusText);
            this.isTenantActive.set(!isActive);
          },
        }),
      );
    } else {
      this._registerSubscription(
        this._tenantService.deleteTenantLink$().subscribe({
          complete: () => {
            this.requestState.completed();
            this._notificationService.addNotification(
              'app.notifications.messages.roles.tenant_deactivated',
              ENotificationCode.success,
            );
            this._emitRoleChange();
          },
          error: (error: HttpErrorResponse) => {
            this.requestState.onError(error.status, error.statusText);
            this.isTenantActive.set(!isActive);
          },
        }),
      );
    }
  }

  public onOwnerToggle(isActive: boolean) {
    if (!this._atLeastOne(this.isTenantActive(), isActive)) {
      return;
    }
    this.isOwnerActive.set(isActive);
    this.requestState.asked();
    if (isActive) {
      this._registerSubscription(
        this._ownerService.createOwnerLink$().subscribe({
          complete: () => {
            this.requestState.completed();
            this._notificationService.addNotification(
              'app.notifications.messages.roles.owner_activated',
              ENotificationCode.success,
            );
            this._emitRoleChange();
          },
          error: (error: HttpErrorResponse) => {
            this.requestState.onError(error.status, error.statusText);
            this.isOwnerActive.set(!isActive);
          },
        }),
      );
    } else {
      this._registerSubscription(
        this._ownerService.deleteOwnerLink$().subscribe({
          complete: () => {
            this.requestState.completed();
            this._notificationService.addNotification(
              'app.notifications.messages.roles.owner_deactivated',
              ENotificationCode.success,
            );
            this._emitRoleChange();
          },
          error: (error: HttpErrorResponse) => {
            this.requestState.onError(error.status, error.statusText);
            this.isOwnerActive.set(!isActive);
          },
        }),
      );
    }
  }

  private _atLeastOne(isTenant: boolean, isOwner: boolean) {
    if (isTenant || isOwner) {
      return true;
    }
    this._notificationService.addNotification(
      'app.user.components.role_selection.at_least_one_role',
      ENotificationCode.error,
    );
    return false;
  }

  private _emitRoleChange() {
    this._updatePermissions();
    this._registerSubscription(this._userService.fetchUserProfile$().subscribe());
    this.roleChange.emit({
      isTenantActive: this.isTenantActive(),
      isOwnerActive: this.isOwnerActive(),
    });
  }

  private _updatePermissions() {
    const roles: string[] = [];
    if (this.isTenantActive()) {
      roles.push('tenant');
    }
    if (this.isOwnerActive()) {
      roles.push('owner');
    }
    this._permissionsService.setGuard({ roles });
  }

  private _fetch() {
    this.requestState.asked();
    this._registerSubscription(
      this._userService.fetchDetails$().subscribe({
        complete: () => this.requestState.completed(),
        error: (error: HttpErrorResponse) => {
          this.requestState.onError(error.status, error.statusText);
        },
      }),
    );
  }
}
